import type {
  DependentRelationship,
  ForeignNationalDependent
} from "../../../api/types/foreignNationalDependent";
import { ageFromBirthday } from "./format";

export const RELATIONSHIP_ORDER: DependentRelationship[] = [
  "SPOUSE",
  "CHILD",
  "PARENT",
  "SIBLING",
  "OTHER"
];

export const RELATIONSHIP_LABEL: Record<DependentRelationship, string> = {
  SPOUSE: "Spouse",
  CHILD: "Child",
  PARENT: "Parent",
  SIBLING: "Sibling",
  OTHER: "Other"
};

export const relationshipLabel = (rel: string | null | undefined): string => {
  const key = (rel ?? "")
    .toString()
    .trim()
    .toUpperCase() as DependentRelationship;
  return RELATIONSHIP_LABEL[key] ?? "Dependent";
};

export const dependentName = (d: ForeignNationalDependent): string =>
  [d.first_name, d.middle_name, d.last_name]
    .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
    .map((p) => p.trim())
    .join(" ");

/** "Child (7 yrs)" for children with a birthday, else just the relationship. */
export const dependentSubtitle = (d: ForeignNationalDependent): string => {
  const label = relationshipLabel(d.relationship);
  if (d.relationship === "CHILD") {
    const age = ageFromBirthday(d.birthday);
    if (age !== null) return `${label} (${age} yrs)`;
  }
  return label;
};

/** Spouse first, then children, parents, siblings, other; then by name. */
export const sortDependents = (
  list: ForeignNationalDependent[]
): ForeignNationalDependent[] => {
  const rank = new Map(RELATIONSHIP_ORDER.map((r, i) => [r, i]));
  return [...list].sort((a, b) => {
    const ra = rank.get(a.relationship) ?? RELATIONSHIP_ORDER.length;
    const rb = rank.get(b.relationship) ?? RELATIONSHIP_ORDER.length;
    if (ra !== rb) return ra - rb;
    const last = (a.last_name ?? "")
      .trim()
      .localeCompare((b.last_name ?? "").trim(), undefined, {
        sensitivity: "base"
      });
    if (last !== 0) return last;
    return (a.first_name ?? "")
      .trim()
      .localeCompare((b.first_name ?? "").trim(), undefined, {
        sensitivity: "base"
      });
  });
};
